import { Component, Input, Output, OnInit, OnChanges, SimpleChanges, EventEmitter } from '@angular/core';
import { Voter } from 'src/app/models/Voter';
import { Common } from 'src/app/shared/common';
import { Union } from 'src/app/models/Union';
import { ElectionColleges } from '../../pap-dto/pap-dto';
import { v4 } from 'uuid';
import { ApiService } from 'src/app/shared/api.service';
import { Assessor } from 'src/app/models/Assessor';
import { ElectionStepLogForm } from '../../../models/Poll';

export interface UnionCandidates {
  union: Union;
  titularCandidates: Array<Voter>;
  substituteCandidates: Array<Voter>;
}

export interface CollegeCandidatesData {
  collegeIndex: number;
  collegeName: string;
  data: Array<UnionCandidates>;
}

@Component({
  selector: 'app-poll-creation-union',
  templateUrl: 'poll-creation-union.component.html',
  styleUrls: ['./poll-creation-union.component.less', '../../../shared/common.less'],
})

export class PollCreationUnionComponent implements OnInit, OnChanges {
  @Input() unions: Array<Union> = [];
  @Input() voters: Array<Voter> = [];
  @Input() colleges: Array<ElectionColleges> = [];
  @Input() assessors: Assessor[] = [];
  @Input() draftId: string;
  @Input() unionValidatedEvent: boolean = false;
  @Input() areAssessorsValidated: boolean = false;
  @Output() unionsChange = new EventEmitter<Array<Union>>();
  @Output() onUnionImageChange = new EventEmitter<Array<any>>();
  @Output() assessorsValidated = new EventEmitter<void>();
  @Input() collegeCandidatesData: Array<CollegeCandidatesData> = [];
  @Output() collegeCandidatesDataChange = new EventEmitter<Array<CollegeCandidatesData>>();
  @Output() unionValidatedEventChange = new EventEmitter<boolean>();
  @Input() validatedColleges: Set<number> = new Set();
  @Output() validatedCollegesChange = new EventEmitter<Set<number>>();
  validatedUnions: Array<Union> = [];
  tempUnions: Array<Union> = [];
  isUnionValid: Array<boolean> = [];
  hasCommonUnion: Array<boolean> = [];
  totalPercentage: number = 0;
  hasMinimumPercentage: boolean;
  displayImageError: boolean;
  votersByCollege: Array<Array<Voter>> = [];

  constructor(
    public common: Common,
    private apiService: ApiService,
  ) { }

  touchedUnions = new Set<number>();

  addList() {
    const newUnion: Union = {
      fullName: '',
      shortName: '',
      logo: '',
      file: '',
      colleges: '',
      commonUnion: [
        { name: '', percentage: 0, parentOrg: null },
        { name: '', percentage: 0, parentOrg: null }
      ],
      associatedColleges: []
    }
    this.tempUnions.push(newUnion);
  }

  tmpOrganizeVotersByCollege() {
    this.votersByCollege = [];

    const denominationToCollegeNumber: { [key: string]: number } = {
      'Collège Unique': 1,
      '1er Collège: ouvriers, employés': 1,
      '2ème Collège: techniciens, agents de maîtrise, ingénieurs et cadres': 2,
      '3ème Collège: ingénieurs et cadres': 3,
      'Autre Collège': 4
    };

    this.colleges.forEach((college, index) => {
      const denomination = college.collegeType?.[0] || '';
      const voterCollegeNumber = denominationToCollegeNumber[denomination];

      if (voterCollegeNumber !== undefined) {
        this.votersByCollege[index] = this.voters.filter(
          voter => voter.college && parseInt(voter.college, 10) === voterCollegeNumber
        );
      } else {
        this.votersByCollege[index] = [];
      }
    });
  }

  organizeVotersByCollege() {
    this.votersByCollege = [];

    this.voters.forEach(voter => {
      if (voter.college) {
        const collegeIndex = parseInt(voter.college, 10) - 1;

        if (collegeIndex >= 0) {
          if (!this.votersByCollege[collegeIndex]) {
            this.votersByCollege[collegeIndex] = [];
          }
          this.votersByCollege[collegeIndex].push(voter);
        }
      }
    });
  }

  areUnionsComplete(): boolean {
    return true;
  }

  onCollegeValidated(collegeIndex: number) {
    this.validatedColleges.add(collegeIndex);
    this.validatedCollegesChange.emit(this.validatedColleges);
    if (this.areAllCollegesValidated()) {
      this.apiService.insertEventLog(this.draftId, new ElectionStepLogForm('Validation', 'candidatesList')).subscribe(res => { });
    }
  }

  onCollegeCandidatesData(event: { collegeIndex: number, data: Array<UnionCandidates> }) {
    const existingIndex = this.collegeCandidatesData.findIndex(c => c.collegeIndex === event.collegeIndex);
    const collegeData: CollegeCandidatesData = {
      collegeIndex: event.collegeIndex,
      collegeName: this.colleges[event.collegeIndex - 1]?.collegeName || `Collège ${event.collegeIndex}`,
      data: event.data
    };

    if (existingIndex > -1) {
      this.collegeCandidatesData[existingIndex] = collegeData;
    } else {
      this.collegeCandidatesData.push(collegeData);
    }

    this.collegeCandidatesDataChange.emit(this.collegeCandidatesData);
  }

  areAllCollegesValidated(): boolean {
    return this.colleges.length > 0 && this.validatedColleges.size === this.colleges.length;
  }

  onAssessorsValidated() {
    this.assessorsValidated.emit();
  }

  isCollegeChecked(union: Union, collegeId: number): boolean {
    return union.associatedColleges?.includes(collegeId) || false;
  }

  onCollegeToggle(union: Union, collegeId: number, index: number): void {
    if (!union.associatedColleges) {
      union.associatedColleges = [];
    }

    const idx = union.associatedColleges.indexOf(collegeId);
    if (idx > -1) {
      union.associatedColleges.splice(idx, 1);
    } else {
      union.associatedColleges.push(collegeId);
    }

    this.onUnionChange(index);
  }

  onUnionChange(index: number) {
    this.isUnionValid[index] = this.isUnionComplete(this.tempUnions[index]);
    this.touchedUnions.add(index);
  }

  onCommonUnionChange(isCommon: boolean, index: number) {
    this.onUnionChange(index);
  }

  onCommonUnionCountChange(count: number, index: number) {
    const union = this.tempUnions[index];
    const currentLength = union.commonUnion.length;
    const targetLength = Math.max(2, Math.min(10, count));

    if (targetLength > currentLength) {
      // Ajouter des éléments
      for (let i = currentLength; i < targetLength; i++) {
        union.commonUnion.push({ name: '', percentage: 0, parentOrg: null });
      }
    } else if (targetLength < currentLength) {
      // Supprimer des éléments
      union.commonUnion.splice(targetLength);
    }

    this.onUnionChange(index);
  }

  validateUnions() {
    this.unionValidatedEvent = true;
    this.unionValidatedEventChange.emit(true);
    this.apiService.insertEventLog(this.draftId, new ElectionStepLogForm('Validation', 'votedList')).subscribe(res => { });
  }

  uploadUnionImage(image: Blob, ext: string, index: number) {
    const pollPayload = new FormData();
    const name = v4() + ext;
    pollPayload.append(name, image);
    // this.isLoading = true;

    this.apiService.saveElectionImage(this.draftId, pollPayload).subscribe((res) => {
      const url = this.apiService.getElectionImageUrl(res[0]);
      // this.isLoading = false;
      this.tempUnions[0].logo = url;
      // this.onUnionImageChange.emit([url, index]);
      // this.setImageUrlToLocation(url, destination, index, candidateIndex);
      // this.markPollChanges();
    });
  }

  removeUnion(index: number) {
    this.tempUnions.splice(index, 1);
    this.isUnionValid.splice(index, 1);
    this.touchedUnions.delete(index);
  }

  isFieldInvalid(index: number, value: string): boolean {
    return this.touchedUnions.has(index) && !value?.trim();
  }

  isUnionComplete(union: Union): boolean {
    if (union.isCommonUnion) {
      this.totalPercentage = union.commonUnion.reduce((sum, item) => sum + (item.percentage || 0), 0);

      // Vérifier que chaque élément a au moins 1% de pourcentage
      this.hasMinimumPercentage = union.commonUnion.every(item => item.percentage >= 1);
      if (!this.hasMinimumPercentage) {
        return false;
      }
      if (this.totalPercentage !== 100) {
        return false;
      }
    }

    if (union.associatedColleges.length == 0) {
      return false;
    }

    if (!union.shortName?.trim()) {
      return false;
    }

    return true;
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      event.preventDefault();
      return false;
    }
    return true;
  }

  trackByIndex(index: number): number {
    return index;
  }

  onFileChanged(event) {
    const displayImage = document.getElementById('uploaded') as HTMLImageElement;
    const uploadImage = document.getElementById('upload-image-icon') as HTMLImageElement;
    const uploadDescription = document.getElementById('description-upload');
    const uploadedImagePanel = document.getElementById('upload-image-wrapper');
    const uploadImagePanel = document.getElementById('upload-panel');
    //this.imageName = document.getElementById('image-name');

    const reader = new FileReader();
    const extensionImage = event[0].name.substring(event[0].name.lastIndexOf('.'));

    const imageBlob = new Blob(event);

    reader.readAsDataURL(imageBlob);
    if (event[0].size > 2097152) {
      this.displayImageError = true;
    }


    const image = new Image();
    const objectUrl = URL.createObjectURL(event[0]);
    image.onload = function () {
      // if (this.displayImageError === false) {
      displayImage.style.display = 'block';
      displayImage.src = URL.createObjectURL(imageBlob);
      uploadImage.style.display = 'none';
      /*uploadedImagePanel.style.display = 'grid';
      uploadImagePanel.style.display = 'none';
      uploadDescription.style.display = 'none';
      this.imageName.style.display = 'block';*/

      //this.onUnionImageChange.emit([extensionImage, imageBlob]);
      // }
      URL.revokeObjectURL(objectUrl);
      this.uploadUnionImage(imageBlob, extensionImage);
    }.bind(this, event);
    image.src = objectUrl;
  }

  addUnionToList(union: Union, index: number): void {
    this.unions.push(union);
    this.tempUnions.splice(index, 1);
    this.isUnionValid.splice(index, 1);
    this.unionsChange.emit(this.unions);
  }

  candidatesByCollege(collegeIndex: number): Array<Voter> {
    return this.votersByCollege[collegeIndex] || [];
  }

  getCollegeCandidatesData(collegeIndex: number): CollegeCandidatesData | undefined {
    return this.collegeCandidatesData.find(c => c.collegeIndex === collegeIndex);
  }

  ngOnInit(): void {
    //this.organizeVotersByCollege();
    this.tmpOrganizeVotersByCollege();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['voters'] && !changes['voters'].firstChange) {
      this.tmpOrganizeVotersByCollege();
      // this.organizeVotersByCollege();
    }
  }
}
